import { useTranslation } from 'react-i18next'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useDeleteTradingPartner } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/hooks/useDeleteTradingPartner'
import type { TradingPartnerRow } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/types'
import { mapMasterDeleteError } from '~/pages/AdminOcctoMaster/tabs/_common/deleteError'

type Props = {
  isOpen: boolean
  row: TradingPartnerRow
  onClose: () => void
  onError: (message: string) => void
  errorMessage: string | undefined
}

/**
 * 取引先マスタ 削除確認ダイアログ。
 * optimistic lock エラー時は onError コールバックで親にエラーを通知する。
 */
export const DeleteTradingPartnerDialog = ({
  isOpen,
  row,
  onClose,
  onError,
  errorMessage,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { mutateAsync, isPending } = useDeleteTradingPartner()

  const handleConfirm = async (): Promise<void> => {
    try {
      await mutateAsync({
        id: row.id,
        // row.updatedAt は list response の proto Timestamp をそのまま round-trip。
        // JS Date を経由すると µs 精度が落ち、backend の DB updated_at (= µs 精度)
        // との optimistic-lock 比較が一致せず Aborted で返る。
        expectedUpdatedAt: row.updatedAt,
      })
      onClose()
      // 削除成功の toast (2026-08-28 デザイナー指摘)。
      showSuccessToast({ title: t('common:toast.deleted') })
    } catch (err) {
      onError(
        mapMasterDeleteError(
          t,
          err,
          'page:adminOcctoMaster.tradingPartner.serverError',
        ),
      )
    }
  }

  return (
    <ConfirmDialog
      body={
        errorMessage ??
        t('page:adminOcctoMaster.tradingPartner.delete.confirmBody', {
          name: row.name,
          shortName: row.shortName,
        })
      }
      confirmLabel={t(
        'page:adminOcctoMaster.tradingPartner.delete.confirmLabel',
      )}
      destructive
      isLoading={isPending}
      isOpen={isOpen}
      onCancel={onClose}
      onConfirm={() => {
        void handleConfirm()
      }}
      title={t('page:adminOcctoMaster.tradingPartner.delete.confirmTitle')}
    />
  )
}
